import { checkEligibility, previewMatch, saveReviewPattern } from "../useLearning";

// The server's refusal codes -> the editor's refusal keys (editorText.refused). Anything else is a failed save.
const REFUSAL_OF = Object.freeze({
  learning_disabled: "learning_disabled",
  not_in_diagnosis: "not_in_diagnosis",
  already_decided: "already_decided",
  not_your_session: "not_your_session",
  review_unavailable: "review_unavailable",
  recording: "recording",
  not_recorded: "not_recorded",
});

/**
 * The editor's `onSubmit` for one chat: saves the pattern against that session and answers with the editor's result shape.
 * A refusal the server explains (learning off, no diagnosis, already decided, not the owner) becomes a refused result; any
 * other failure is thrown, and the editor shows "could not save" and keeps the preview (a 422 carries the blocking `issues`,
 * which the editor lists instead). `recording` (the diagnosis ended and
 * its record is still being written) is not a refusal: the editor waits for it and keeps the preview.
 */
export function submitPattern(sessionId) {
  return async (payload) => {
    try {
      await saveReviewPattern({ ...payload, session_id: sessionId });
      return { kind: "learned" };
    } catch (error) {
      const reason = REFUSAL_OF[error && error.message];
      if (reason) return { kind: "refused", reason };
      throw error;
    }
  };
}

/**
 * Check whether the editor can save for this session. Returns {canSave, reason} or null (endpoint missing).
 */
export async function fetchEligibility(sessionId) {
  if (!sessionId) return { canSave: false, reason: null };
  try {
    const data = await checkEligibility(sessionId);
    if (data === null) return null; // endpoint not there
    return { canSave: !!data.can_save, reason: data.reason || null };
  } catch {
    return { canSave: false, reason: "review_unavailable" };
  }
}

/**
 * Ask the server how each line of the draft matches what Arta already knows. Resolves with the response body; rejects when the
 * request fails, is aborted or the endpoint is missing, so the caller falls back to "everything new".
 */
export async function fetchPreviewMatch(sessionId, request, signal) {
  const data = await previewMatch(sessionId, request, signal);
  if (!data) throw new Error("preview_match_unavailable");
  return data;
}
